<script>
  import CheckRow from "./CheckRow.vue";
  import MainCard from "./Maincard.vue";

  export default {
    name: "SelectLocation",
    components: { CheckRow, MainCard },

    props: {
      className: {
        type: String,
        default: "",
      },
      onApply: {
        type: Function,
        default: null,
      },
      locations: {
        type: Array,
        default: () => [],
      },

      cities: {
        type: Array,
        default: () => null,
      },
      offices: {
        type: Array,
        default: () => null,
      },
      loadCities: {
        type: Function,
        default: null,
      },
      loadOffices: {
        type: Function,
        default: null,
      },
    },

    data() {
      return {
        selectedCities: {},
        selectedOffices: {},
        innerCities: [],
        innerOffices: [],
        loadedOnce: false,
      };
    },

    computed: {
      citiesList() {
        return Array.isArray(this.cities) ? this.cities : this.innerCities;
      },
      officesList() {
        return Array.isArray(this.offices) ? this.offices : this.innerOffices;
      },
    },

    created() {
      this.bootstrapData();
    },

    watch: {
      locations: {
        handler() {
          this.hydrateFromLocations();
        },
        deep: true,
        immediate: true,
      },
      citiesList() {
        this.hydrateFromLocations();
      },
      officesList() {
        this.hydrateFromLocations();
      },

    },

    methods: {
      async bootstrapData() {
        const needCities = !Array.isArray(this.cities);
        const needOffices = !Array.isArray(this.offices);

        if (!needCities && !needOffices) return;

        const loaders = [];
        if (needCities && typeof this.loadCities === "function") {
          loaders.push(
            Promise.resolve(this.loadCities()).then((arr) => {
              this.innerCities = Array.isArray(arr) ? arr : [];
            }),
          );
        }
        if (needOffices && typeof this.loadOffices === "function") {
          loaders.push(
            Promise.resolve(this.loadOffices()).then((arr) => {
              this.innerOffices = Array.isArray(arr) ? arr : [];
            }),
          );
        }
        await Promise.all(loaders);
        this.hydrateFromLocations();
      },

      officesByCity(cityId) {
        return this.officesList.filter((o) => o.cityId === cityId);
      },

      shouldShowOffices(cityId) {
        return this.officesByCity(cityId).length > 1;
      },

      updateDateSelect(prev, item, isSelected) {
        const clone = { ...prev };
        if (!isSelected) {
          delete clone[item._id];
        } else {
          clone[item._id] = item;
        }
        return clone;
      },

      handleCityChange(city, emit = true) {
        const isSelected = !this.selectedCities[city._id];
        const officesOfCity = this.officesByCity(city._id);

        this.selectedCities = this.updateDateSelect(this.selectedCities, city, isSelected);

        let newOffices = this.selectedOffices;
        officesOfCity.forEach((office) => {
          newOffices = this.updateDateSelect(newOffices, office, isSelected);
        });
        this.selectedOffices = newOffices;
        if (emit) this.handleApply();
      },

      handleOfficeChange(office, city, emit = true) {
        const isSelected = !this.selectedOffices[office._id];

        const allSelected = this.officesByCity(city._id).every((current) => (current._id === office._id ? isSelected : !!this.selectedOffices[current._id]));

        this.selectedOffices = this.updateDateSelect(this.selectedOffices, office, isSelected);
        this.selectedCities = this.updateDateSelect(this.selectedCities, city, allSelected);
        if (emit) this.handleApply();
      },

      getLocationsSelected() {
        const cities = Object.values(this.selectedCities);
        const offices = Object.values(this.selectedOffices);
        const officesToSet = offices.filter((office) => !cities.some((city) => city._id === office.cityId));
        return [...cities, ...officesToSet];
      },

      hydrateFromLocations() {
        if (!this.citiesList.length && !this.officesList.length) return;
        this.selectedCities = {};
        this.selectedOffices = {};

        (this.locations || []).forEach((location) => {
          const isOffice = !!location && !!location.cityId;
          if (!isOffice) {
            this.handleCityChange(location, false);
            return;
          }
          const cityOffice = this.citiesList.find((c) => c._id === location.cityId);
          if (cityOffice) {
            this.handleOfficeChange(location, cityOffice, false);
          }
        });

        this.loadedOnce = true;
      },

      handleApply() {
        const selected = this.getLocationsSelected();
        this.$emit("apply", selected);
        if (typeof this.onApply === "function") this.onApply(selected);
      },
    },
  };
</script>

<template>
  <div :class="['selectLocation', className]">
    <div v-for="city in citiesList" :key="city._id" class="selectLocation__cityWrapper">
      <CheckRow :label="city.cityName" :checkValue="!!selectedCities[city._id]" @change="() => handleCityChange(city)" />
      <div v-if="shouldShowOffices(city._id)" class="selectLocation__officeWrapper">
        <CheckRow v-for="office in officesByCity(city._id)" :key="office._id" :label="office.officeName" :checkValue="!!selectedOffices[office._id]" @change="() => handleOfficeChange(office, city)" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .selectLocation {
    z-index: 20;
    background: #fff;
    padding: 16px;
    box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.1019607843);
    border-radius: 10px;

    &__header {
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }

    &__description {
      margin: 0;
      font-size: 14px;
      color: #4b5563;
    }

    &__cityWrapper {
      margin-bottom: 10px;
    }

    &__officeWrapper {
      display: flex;
      padding-left: 20px;
      flex-wrap: wrap;
      gap: 15px;
    }
  }
</style>
